import React, { useState, useRef, useCallback } from 'react';
import PropTypes from 'prop-types';
import { Text, Icon } from '@reservamos/elements';

const TOOLTIP_OFFSET = 8;
const TOOLTIP_MIN_MARGIN = 8;
const TOOLTIP_VIEWPORT_MARGIN = 8;
const TOOLTIP_MIN_SPACE_BELOW = 80;

/**
 * Calculate the fixed tooltip position relative to the viewport.
 * Flips the tooltip above the trigger when there is insufficient space below.
 * @param {DOMRect} rect - The DOMRect object of the trigger element.
 * @param {number} width - Tooltip width in pixels.
 * @returns {{ top: number, left: number, showAbove: boolean }} The calculated position and flip flag.
 */
const getTooltipPosition = (rect, width) => {
  const left = rect.left + rect.width / 2 - width / 2;
  const maxLeft = window.innerWidth - width - TOOLTIP_VIEWPORT_MARGIN;
  const spaceBelow = window.innerHeight - rect.bottom - TOOLTIP_OFFSET;
  const showAbove = spaceBelow < TOOLTIP_MIN_SPACE_BELOW;

  return {
    top: showAbove ? rect.top - TOOLTIP_OFFSET : rect.bottom + TOOLTIP_OFFSET,
    left: Math.max(TOOLTIP_MIN_MARGIN, Math.min(left, maxLeft)),
    showAbove,
  };
};

/**
 * FixedTooltip component.
 * Renders an icon that, when hovered, shows a tooltip using position: fixed
 * to escape ancestor overflow containers.
 * @param {object} props - content, width.
 * @param {string|string[]} props.content - Tooltip text content. Accepts a string or array of strings.
 * @param {number} [props.width=220] - Tooltip width in pixels.
 * @returns {JSX.Element} The rendered fixed tooltip component.
 */
const FixedTooltip = ({ content, width }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0, showAbove: false });
  const triggerRef = useRef(null);

  const handleMouseEnter = useCallback(() => {
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return;

    setPos(getTooltipPosition(rect, width));
    setVisible(true);
  }, [width]);

  const handleMouseLeave = useCallback(() => {
    setVisible(false);
  }, []);

  return (
    <>
      <div
        ref={triggerRef}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="presentation"
        className="inline-flex cursor-pointer"
      >
        <Icon size="S" type="badgeQuestion" />
      </div>
      {visible && (
        <div
          className="fixed bg-white rounded shadow-[2px_2px_10px_rgba(0,0,0,0.5)] p-2.5 z-[9999]"
          style={{
            top: pos.top,
            left: pos.left,
            width,
            transform: pos.showAbove ? 'translateY(-100%)' : undefined,
          }}
        >
          {[].concat(content).map((line) => (
            <Text key={line} size="XS" color="grayMedium">
              {line}
            </Text>
          ))}
        </div>
      )}
    </>
  );
};

FixedTooltip.propTypes = {
  content: PropTypes.oneOfType([PropTypes.string, PropTypes.arrayOf(PropTypes.string)]).isRequired,
  width: PropTypes.number,
};

FixedTooltip.defaultProps = {
  width: 220,
};

export default FixedTooltip;
